import { Button, Text } from '@lbcamden/lib-react';
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';
import { getQueryKey } from '@trpc/react-query';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { routes } from '~/client/config/routes';
import { useHandleAuth, useIsUserRegistered } from '~/client/hooks/authHooks';
import { useCurrentUser } from '~/client/hooks/storeHooks';
import { useDeviceId } from '~/client/hooks/useDeviceId';
import { trackError } from '~/client/lib/appInsights';
import { logout } from '~/client/lib/auth';
import { queryClient } from '~/client/lib/queryClient';
import { api } from '~/client/lib/trpc';
import { useAppStore } from '~/client/store/app/appStore';

import { SplashPage } from '../SplashPage';
import { defaultCopy, errorCopy, unsupportedDeviceCopy } from './config';

export const AuthRegistrationPage: FunctionComponent = () => {
  const navigate = useNavigate();

  const deviceId = useDeviceId();

  const user = useCurrentUser();

  const loginRedirectUri = useAppStore((state) => state.loginRedirectUri);

  const [isSupported, setIsSupported] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { isRegistered } = useIsUserRegistered();

  const { mutateAsync: generateRegistration } = api.generateRegistration.useMutation();
  const { mutateAsync: verifyRegistration } = api.verifyRegistration.useMutation();

  const { handleAuth } = useHandleAuth({
    onAuthFailure: logout,
  });

  const supportedDeviceCopy = hasError ? errorCopy : defaultCopy;
  const copy = isSupported ? supportedDeviceCopy : unsupportedDeviceCopy;

  const canRegister = isSupported && !hasError;

  const checkIsSupported = () => setIsSupported(browserSupportsWebAuthn());

  const retry = () => {
    checkIsSupported();
    setHasError(false);
  };

  const handleRegistration = async () => {
    setLoading(true);
    try {
      const registrationOpts = await generateRegistration({ deviceId });
      const attResp = await startRegistration({ optionsJSON: registrationOpts });
      await verifyRegistration(attResp);
      await handleAuth(); // Required in order to store the necessary data for client-side auth
      void queryClient.invalidateQueries({ queryKey: getQueryKey(api.isRegistered) });
      void navigate(loginRedirectUri || routes.home, { replace: true });
    } catch (err) {
      console.error({
        err: (err as Error).toString(),
        errString: JSON.stringify(err, null, 2),
      });
      trackError(err, { userId: user?.id, registrationError: true });
      setHasError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!user) {
      void navigate(routes.login, { replace: true });
      return;
    }
    if (isRegistered) {
      void navigate(routes.home, { replace: true });
    }
  }, [user, isRegistered]);

  useEffect(checkIsSupported, []);

  const signOutButton = (
    <Button $variant="tertiary" $color="primary" onClick={logout}>
      Sign out
    </Button>
  );

  const footerButton = canRegister ? null : signOutButton;

  return (
    <SplashPage footerContent={footerButton}>
      <Text variant="h1">{copy.title}</Text>
      <Text css={{ whiteSpace: 'pre-wrap' }} variant="lead">
        {copy.message}
      </Text>
      {canRegister && (
        <Button loading={loading} onClick={handleRegistration}>
          Continue
        </Button>
      )}
      {!canRegister && (
        <Button loading={loading} onClick={retry}>
          Try again
        </Button>
      )}
    </SplashPage>
  );
};
